import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTransition, animated } from '@react-spring/web';

import { Modal, type ModalProps } from '@app/shared/ui/organisms/Common';
import { Settlement } from '@app/shared/ui/molecules/Home';
import { Button, type ButtonProps, Heading, Paragraph } from '@app/shared/ui/atoms/Common';
import { cn, formatTonIncome } from '@app/shared/utils/common';
import { Settlement as SettlementType } from '@app/store/slices/settlements/settlementsTypes';
import { SettlementModalAction } from '@app/shared/types/pages/home';
import { cookieImage, diamondIcon } from '@app/assets';
import { PriceType } from '@app/shared/types/common';
import { Page } from '@app/routes';
import { BackButton } from '@app/shared/ui/molecules/Common';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';

interface SettlementModalProps extends Pick<ModalProps, 'isOpen' | 'onOpenChange'> {
  settlement: SettlementType;
  actionType?: SettlementModalAction;
  onBoostBtnClick: (settlement: SettlementType, paymentType: PriceType) => void;
  onBuyBtnClick: (settlement: SettlementType) => void;
  isBoostBtnDisabled: boolean;
  isBuyBtnDisabled: boolean;
  isBoosting?: boolean;
  toggleIsBoosting?: () => void;
}

interface BadgeLabelProps {
  children: ReactNode;
}

type BadgeProps = BadgeLabelProps;

function BadgeLabel({ children }: BadgeLabelProps) {
  return (
    <Paragraph variant="white" size="xs" className="text-start">
      {children}
    </Paragraph>
  );
}

function Badge({ children }: BadgeProps) {
  return (
    <div className="rounded-[9px] bg-[#FFFFFF66] px-3.5 py-1 text-base font-bold">{children}</div>
  );
}

function ActionButton({ className, children, onClick, disabled }: Omit<ButtonProps, 'shadow'>) {
  const { t } = useTranslation();

  return (
    <Button
      shadow="blue"
      onClick={onClick}
      disabled={disabled}
      className={cn('flex items-center justify-center gap-2', className)}
    >
      {disabled ? t('common.wait') : children}
    </Button>
  );
}

export function SettlementModal({
  isOpen,
  onOpenChange,
  settlement,
  onBoostBtnClick,
  onBuyBtnClick,
  actionType,
  isBoostBtnDisabled,
  isBuyBtnDisabled,
  isBoosting,
  toggleIsBoosting,
}: SettlementModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((state) => state.tutorial);
  const { pathname } = useLocation();

  const transitions = useTransition(isBoosting, {
    from: { opacity: 0, transform: 'translateY(10%)' },
    enter: { opacity: 1, transform: 'translateY(0%)' },
    leave: { opacity: 0, transform: 'translateY(-10%)' },
    config: { tension: 220, friction: 22 },
  });

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentClassName="bg-[#0A78E6BF]"
      hiddenTitle="Settlement"
    >
      {isBoosting && (
        <div className="absolute left-4 top-6">
          <BackButton onClick={toggleIsBoosting} color="white" shouldNavigate={false} iconOnly />
        </div>
      )}

      <div className="flex flex-col items-center gap-5">
        <Settlement
          imageUrl={settlement.noBgImageUrl ?? ''}
          name={settlement.name}
          price={settlement.price}
          isLocked={settlement.isLocked}
          className={!settlement.isPurchased || settlement.isLocked ? 'grayscale' : ''}
        />

        <Heading level={2} className="text-center">
          {settlement.name}
        </Heading>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-5 opacity-[0.84]">
            <Paragraph variant="white" className="text-center font-bold">
              {t('home.settlement_modal.description')}
            </Paragraph>

            {settlement.isPurchased && (
              <div className="grid grid-cols-[1fr_auto] items-center gap-2.5">
                <BadgeLabel>{t('home.settlement_modal.total_income')}</BadgeLabel>
                <Badge>{formatTonIncome(settlement.tonIncome)} 💎</Badge>

                <BadgeLabel>{t('home.settlement_modal.purchased_count')}</BadgeLabel>
                <Badge>{settlement.pandas} 🐼</Badge>
              </div>
            )}
          </div>

          {actionType === 'buy' && (
            <ActionButton
              className="bg-[#FFFFFF30]"
              disabled={isBuyBtnDisabled}
              onClick={() => {
                onBuyBtnClick(settlement);
              }}
            >
              {t('settlements.build')}
            </ActionButton>
          )}

          {actionType === 'boost' && (
            <div className="relative w-full min-h-[120px]">
              {transitions((style, item) => (
                <animated.div
                  style={style}
                  className="absolute inset-0 flex flex-col gap-4 justify-center"
                >
                  {item ? (
                    <>
                      <ActionButton
                        className="bg-[#FFFFFF30]"
                        disabled={isBoostBtnDisabled}
                        onClick={() => {
                          onBoostBtnClick(settlement, PriceType.COOKIE);
                        }}
                      >
                        {t('home.settlement_modal.boost_settlement_for')}{' '}
                        {settlement.boostCookiePrice}
                        <img src={cookieImage} className="h-4" alt="cookie" />
                        <ChevronsUp size="24" />
                      </ActionButton>

                      <ActionButton
                        disabled={isBoostBtnDisabled}
                        onClick={() => {
                          onBoostBtnClick(settlement, PriceType.TON);
                        }}
                      >
                        {t('home.settlement_modal.boost_settlement_for')} {settlement.boostTonPrice}
                        <img src={diamondIcon} className="h-4" alt="diamond" />
                        <ChevronsUp size="24" />
                      </ActionButton>
                    </>
                  ) : (
                    <>
                      <ActionButton className="bg-[#FFFFFF30]" onClick={toggleIsBoosting}>
                        {t('home.settlement_modal.boost_settlement')}
                        <ChevronsUp size="24" />
                      </ActionButton>

                      {settlement.hasDetails && (
                        <ActionButton
                          onClick={() => {
                            if (
                              tutorial.step === 'HOME_SETTLEMENT_1' &&
                              settlement.nodeLevel === 0 &&
                              pathname === '/'
                            ) {
                              dispatch(tutorialActions.onSettlementClick());
                            }
                          }}
                        >
                          <Link
                            to={Page.SETTLEMENT_DETAILS.replace(':id', String(settlement.id))}
                            className="flex items-center justify-center gap-2"
                          >
                            {t('home.settlement_modal.enter_settlement')}
                            <ChevronRight size="24" />
                          </Link>
                        </ActionButton>
                      )}
                    </>
                  )}
                </animated.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
